import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiPhone, FiChevronDown } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);

  const navItems = [
    { name: 'Home', href: '#/' },
    { 
      name: 'Services', 
      href: '#/services',
      hasDropdown: true,
      dropdownItems: [
        { name: 'All Services', href: '#/services' },
        { name: 'Property Management', href: '#/services/property-management' },
        { name: 'Tenant Placement', href: '#/services/tenant-placement' },
        { name: 'Maintenance Services', href: '#/services/maintenance-services' },
        { name: 'Financial Reporting', href: '#/services/financial-reporting' },
        { name: 'Real Estate Investment', href: '#/services/real-estate-investment' },
        { name: 'Legal Compliance', href: '#/services/legal-compliance' },
        { name: 'Residential', href: '#/residential' },
        { name: 'Commercial', href: '#/commercial' }
      ]
    },
    { name: 'Service Areas', href: '#/service-areas' },
    { name: 'About', href: '#/about' },
    { name: 'Contact', href: '#/contact' }
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div whileHover={{ scale: 1.05 }} className="flex-shrink-0">
            <h1 className="text-2xl font-bold text-gray-900">
              <span className="text-blue-600">DGM</span> Property Management
            </h1>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsServicesDropdownOpen(true)}
                    onMouseLeave={() => setIsServicesDropdownOpen(false)}
                  >
                    <motion.a
                      href={item.href}
                      whileHover={{ y: -2 }}
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 flex items-center gap-1"
                    >
                      {item.name}
                      <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                    </motion.a>
                    {isServicesDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-2"
                      >
                        {item.dropdownItems.map((dropdownItem) => (
                          <a
                            key={dropdownItem.name}
                            href={dropdownItem.href}
                            className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                          >
                            {dropdownItem.name}
                          </a>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ) : (
                  <motion.a
                    href={item.href}
                    whileHover={{ y: -2 }}
                    className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                  >
                    {item.name}
                  </motion.a>
                )}
              </div>
            ))}
            <motion.a
              href="tel:587-800-1215"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
            >
              <SafeIcon icon={FiPhone} className="w-4 h-4" />
              587-800-1215
            </motion.a>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-blue-600"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden py-4 border-t border-gray-100"
          >
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <div>
                      <div className="text-gray-700 font-medium mb-2">
                        {item.name}
                      </div>
                      <div className="ml-4 space-y-2">
                        {item.dropdownItems.map((dropdownItem) => (
                          <a
                            key={dropdownItem.name}
                            href={dropdownItem.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="block text-gray-600 hover:text-blue-600 transition-colors duration-200"
                          >
                            {dropdownItem.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                    >
                      {item.name}
                    </a>
                  )}
                </div>
              ))}
              <a
                href="tel:587-800-1215"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 w-fit"
              >
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                587-800-1215
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;